import Cookies from 'universal-cookie';

const IDENTIFIER_COOKIE_KEY = 'fortellis_identifier_id';
const IDENTIFIER_STORAGE_KEY = 'fortellis_identifier_id';
const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60;

/**
 * Returns the root cookie domain (e.g. '.fortellis.io' or '.example.com') so cookies are shared across subdomains,
 * or undefined for localhost, IP addresses, or single-label hostnames.
 */
export const getCookieDomain = (
  hostname = typeof window !== 'undefined' ? window.location?.hostname : ''
) => {
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  if (hostname === 'fortellis.io' || hostname.endsWith('.fortellis.io')) {
    return '.fortellis.io';
  }
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return undefined;
};

/**
 * Generates an RFC4122 v4 UUID.
 * Uses window.crypto.randomUUID() when available, with a random bitwise fallback.
 */
export const generateUUID = () => {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    try {
      return window.crypto.randomUUID();
    } catch (e) {
      // Fall through to fallback
    }
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Retrieves the persistent identifier UUID from the root-domain cookie or localStorage,
 * or generates, saves, and syncs a new one across both cookie and localStorage.
 */
export const getOrGenerateIdentifierId = () => {
  try {
    const cookies = new Cookies();
    let identifierId = cookies.get(IDENTIFIER_COOKIE_KEY);

    // If not found in cookies, check localStorage
    if (!identifierId && typeof window !== 'undefined' && window.localStorage) {
      identifierId = window.localStorage.getItem(IDENTIFIER_STORAGE_KEY);
    }

    // If still not found, generate a new UUID
    if (!identifierId) {
      identifierId = generateUUID();
    }

    // Persist/sync to root cookie
    const domain = getCookieDomain();
    const isSecure =
      typeof window !== 'undefined' && window.location?.protocol === 'https:';

    cookies.set(IDENTIFIER_COOKIE_KEY, identifierId, {
      path: '/',
      maxAge: ONE_YEAR_IN_SECONDS,
      domain,
      secure: isSecure,
      sameSite: 'lax'
    });

    // Also sync to localStorage for redundancy
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(IDENTIFIER_STORAGE_KEY, identifierId);
    }

    return identifierId;
  } catch (e) {
    return generateUUID();
  }
};

/**
 * Retrieves the orgUnitId for the given entity or from the entities list by targetEntityId.
 * Returns null if not found or if orgUnitId is empty/non-string.
 */
export const getOrgUnitId = (entity, entities, targetEntityId) => {
  if (
    entity?.orgUnitId &&
    typeof entity.orgUnitId === 'string' &&
    entity.orgUnitId.trim() !== ''
  ) {
    return entity.orgUnitId.trim();
  }
  if (Array.isArray(entities) && targetEntityId) {
    const matched = entities.find(org => org?.id === targetEntityId);
    if (
      matched?.orgUnitId &&
      typeof matched.orgUnitId === 'string' &&
      matched.orgUnitId.trim() !== ''
    ) {
      return matched.orgUnitId.trim();
    }
  }
  return null;
};

/**
 * Converts a role string to title case while preserving separators.
 */
export const toTitleCaseRole = role => {
  if (typeof role !== 'string') {
    return null;
  }

  const trimmed = role.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/\b\w+/g, token => {
    const lower = token.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  });
};

/**
 * Resolves analytics/GTM roles with no fallback role.
 * Returns null when roles are missing or empty.
 */
export const resolveAnalyticsRoles = roles => {
  if (!Array.isArray(roles)) {
    return null;
  }

  const normalizedRoles = roles
    .map(toTitleCaseRole)
    .filter(role => typeof role === 'string' && role.length > 0);

  return normalizedRoles.length > 0 ? normalizedRoles : null;
};
